import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SITE_BLOCKS } from "@/lib/siteContent";
import { RotateCcw, Save } from "lucide-react";

type Row = { page_slug: string; block_key: string; value: string; updated_at?: string };

const PAGE_LABELS: Record<string, string> = {
  home: "Home",
  about: "About Us",
  insights: "Insights",
  events: "Events",
  opportunities: "Opportunities",
};

const PageContentEditor = () => {
  const [rows, setRows] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof SITE_BLOCKS>();
    for (const b of SITE_BLOCKS) {
      if (!map.has(b.page_slug)) map.set(b.page_slug, []);
      map.get(b.page_slug)!.push(b);
    }
    return Array.from(map.entries());
  }, []);

  const callApi = async (action: string, payload: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke("manage-site-content", {
      body: { action, ...payload },
    });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const composeKey = (slug: string, key: string) => `${slug}::${key}`;

  const load = async () => {
    setLoading(true);
    try {
      const data = await callApi("list");
      const next: Record<string, string> = {};
      for (const r of (data.blocks as Row[]) ?? []) {
        next[composeKey(r.page_slug, r.block_key)] = r.value;
      }
      // Seed any missing keys with fallback so the textarea isn't empty
      for (const b of SITE_BLOCKS) {
        const k = composeKey(b.page_slug, b.block_key);
        if (!(k in next)) next[k] = b.fallback;
      }
      setRows(next);
      setDirty({});
    } catch (e: any) {
      toast.error(e.message ?? "Failed to load site content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (slug: string, key: string) => {
    const k = composeKey(slug, key);
    setSavingKey(k);
    try {
      await callApi("upsert", { page_slug: slug, block_key: key, value: rows[k] ?? "" });
      toast.success("Saved");
      setDirty((d) => ({ ...d, [k]: false }));
    } catch (e: any) {
      toast.error(e.message ?? "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const handleReset = async (slug: string, key: string, fallback: string) => {
    if (!confirm("Reset this block to its default copy?")) return;
    const k = composeKey(slug, key);
    setSavingKey(k);
    try {
      await callApi("reset", { page_slug: slug, block_key: key });
      setRows((r) => ({ ...r, [k]: fallback }));
      setDirty((d) => ({ ...d, [k]: false }));
      toast.success("Reset to default");
    } catch (e: any) {
      toast.error(e.message ?? "Reset failed");
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-8">Loading site content…</div>;
  }

  return (
    <div>
      <div className="mb-5">
        <h2 className="font-display text-xl font-semibold">Page Content</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Edit the headlines, intros, and labels shown across the main marketing pages. Changes go live as soon as you save (cached for ~5 minutes).
        </p>
      </div>

      <Accordion type="multiple" defaultValue={["home"]} className="space-y-3">
        {grouped.map(([slug, blocks]) => (
          <AccordionItem
            key={slug}
            value={slug}
            className="bg-card border border-border rounded-xl px-4"
          >
            <AccordionTrigger className="hover:no-underline">
              <span className="font-display font-semibold">
                {PAGE_LABELS[slug] ?? slug}{" "}
                <span className="text-xs text-muted-foreground font-normal ml-2">
                  {blocks.length} block{blocks.length === 1 ? "" : "s"}
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-5 pb-2">
                {blocks.map((b) => {
                  const k = composeKey(b.page_slug, b.block_key);
                  const val = rows[k] ?? "";
                  const isDirty = !!dirty[k];
                  const saving = savingKey === k;
                  return (
                    <div key={k} className="border-t border-border pt-4 first:border-t-0 first:pt-0">
                      <div className="flex items-center justify-between gap-3 mb-1.5">
                        <Label className="text-sm font-medium">{b.label}</Label>
                        <code className="text-[10px] text-muted-foreground">{b.block_key}</code>
                      </div>
                      {b.multiline ? (
                        <Textarea
                          value={val}
                          rows={Math.min(6, Math.max(2, Math.ceil(val.length / 90)))}
                          onChange={(e) => {
                            setRows((r) => ({ ...r, [k]: e.target.value }));
                            setDirty((d) => ({ ...d, [k]: true }));
                          }}
                          className="bg-background border-border"
                        />
                      ) : (
                        <Input
                          value={val}
                          onChange={(e) => {
                            setRows((r) => ({ ...r, [k]: e.target.value }));
                            setDirty((d) => ({ ...d, [k]: true }));
                          }}
                          className="bg-background border-border"
                        />
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          size="sm"
                          onClick={() => handleSave(b.page_slug, b.block_key)}
                          disabled={saving || !isDirty}
                          className="bg-primary text-primary-foreground"
                        >
                          <Save className="h-3.5 w-3.5 mr-1.5" />
                          {saving ? "Saving…" : "Save"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleReset(b.page_slug, b.block_key, b.fallback)}
                          disabled={saving}
                          className="text-muted-foreground"
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                          Reset to default
                        </Button>
                        {isDirty && (
                          <span className="text-xs text-amber-500 ml-1">Unsaved changes</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default PageContentEditor;